/**
 * Cognito Authentication Service
 * Wraps AWS Amplify Auth functions for use with the application
 */
import { signIn, signOut, signUp, confirmSignUp, resendSignUpCode, getCurrentUser, fetchAuthSession, resetPassword, confirmResetPassword } from 'aws-amplify/auth';

/**
 * Check if Cognito is configured
 */
export const isCognitoConfigured = () => {
    return !!(process.env.REACT_APP_COGNITO_USER_POOL_ID && process.env.REACT_APP_COGNITO_APP_CLIENT_ID);
};

/**
 * Sign in with email and password
 */
export const cognitoSignIn = async (email, password) => {
    try {
        // Sign out any existing user first to avoid "already signed in" error
        try {
            await signOut();
        } catch (e) {
            // Ignore sign out errors (user might not be signed in)
        }

        const result = await signIn({
            username: email,
            password: password,
            options: {
                authFlowType: 'USER_PASSWORD_AUTH'
            }
        });

        if (result.isSignedIn) {
            // Get the session to retrieve tokens
            const session = await fetchAuthSession();
            return {
                success: true,
                user: result,
                tokens: session.tokens
            };
        }

        // Handle challenges (e.g., NEW_PASSWORD_REQUIRED, MFA)
        if (result.nextStep) {
            return {
                success: false,
                challengeName: result.nextStep.signInStep,
                session: result
            };
        }

        return { success: true, user: result };
    } catch (error) {
        console.error('Cognito sign in error:', error);
        throw error;
    }
};

/**
 * Sign out the current user
 */
export const cognitoSignOut = async () => {
    try {
        await signOut();
        return { success: true };
    } catch (error) {
        console.error('Cognito sign out error:', error);
        throw error;
    }
};

/**
 * Sign up a new user
 */
export const cognitoSignUp = async (email, password, name) => {
    try {
        const result = await signUp({
            username: email,
            password: password,
            options: {
                userAttributes: {
                    email: email,
                    name: name || email.split('@')[0]
                }
            }
        });

        return {
            success: true,
            isConfirmed: result.isSignUpComplete,
            nextStep: result.nextStep
        };
    } catch (error) {
        console.error('Cognito sign up error:', error);
        throw error;
    }
};

/**
 * Confirm sign up with verification code
 */
export const cognitoConfirmSignUp = async (email, code) => {
    try {
        const result = await confirmSignUp({
            username: email,
            confirmationCode: code
        });

        return { success: result.isSignUpComplete };
    } catch (error) {
        console.error('Cognito confirm sign up error:', error);
        throw error;
    }
};

/**
 * Resend sign up verification code
 */
export const cognitoResendSignUpCode = async (email) => {
    try {
        await resendSignUpCode({ username: email });
        return { success: true };
    } catch (error) {
        console.error('Cognito resend sign up code error:', error);
        throw error;
    }
};

/**
 * Get the current authenticated user
 */
export const cognitoGetCurrentUser = async () => {
    try {
        const user = await getCurrentUser();
        return user;
    } catch (error) {
        // User not authenticated
        return null;
    }
};

/**
 * Get the current session tokens (ID token, access token)
 */
export const cognitoGetSession = async () => {
    try {
        const session = await fetchAuthSession();
        return session;
    } catch (error) {
        console.error('Error fetching session:', error);
        return null;
    }
};

/**
 * Get the ID token for API authentication
 */
export const cognitoGetIdToken = async () => {
    try {
        const session = await fetchAuthSession();
        return session.tokens?.idToken?.toString() || null;
    } catch (error) {
        return null;
    }
};

/**
 * Get the access token
 */
export const cognitoGetAccessToken = async () => {
    try {
        const session = await fetchAuthSession();
        return session.tokens?.accessToken?.toString() || null;
    } catch (error) {
        return null;
    }
};

/**
 * Initiate password reset
 */
export const cognitoForgotPassword = async (email) => {
    try {
        const result = await resetPassword({ username: email });
        return {
            success: true,
            nextStep: result.nextStep
        };
    } catch (error) {
        console.error('Cognito forgot password error:', error);
        throw error;
    }
};

/**
 * Complete password reset with code
 */
export const cognitoConfirmForgotPassword = async (email, code, newPassword) => {
    try {
        await confirmResetPassword({
            username: email,
            confirmationCode: code,
            newPassword: newPassword
        });
        return { success: true };
    } catch (error) {
        console.error('Cognito confirm forgot password error:', error);
        throw error;
    }
};

/**
 * Check if user is authenticated
 */
export const cognitoIsAuthenticated = async () => {
    try {
        const user = await getCurrentUser();
        return !!user;
    } catch {
        return false;
    }
};

const cognitoAuth = {
    isCognitoConfigured,
    signIn: cognitoSignIn,
    signOut: cognitoSignOut,
    signUp: cognitoSignUp,
    confirmSignUp: cognitoConfirmSignUp,
    resendSignUpCode: cognitoResendSignUpCode,
    getCurrentUser: cognitoGetCurrentUser,
    getSession: cognitoGetSession,
    getIdToken: cognitoGetIdToken,
    getAccessToken: cognitoGetAccessToken,
    forgotPassword: cognitoForgotPassword,
    confirmForgotPassword: cognitoConfirmForgotPassword,
    isAuthenticated: cognitoIsAuthenticated
};

export default cognitoAuth;
